import { lazy } from 'react'
import Roles from 'config/Roles'
import {
  Brain,
  Briefcase,
  Calendar,
  ChartPie,
  ClipboardList,
  CloudUpload,
  CreditCard,
  DollarSign,
  LayoutDashboard,
  PiggyBank,
  Settings,
  Target,
  TrendingUp,
  Users as UsersIcon,
  Wrench
} from 'lucide-react'
import { RoutesMap } from 'ts/types/common'

const Login = lazy(() => import('Authentication/components/Login'))
const ForgotPassword = lazy(() => import('Authentication/components/ForgotPassword'))
const ResetPassword = lazy(() => import('Authentication/components/ResetPassword'))

const Summary = lazy(() => import('ts/modules/summary/Summary'))
const VendorManagement = lazy(() => import('VendorManagement/VendorManagement'))
const Spaces = lazy(() => import('Spaces/components/Spaces'))
const Budget = lazy(() => import('VendorManagement/Budget/components/Budget'))
const Pricing = lazy(() => import('VendorManagement/Pricing/components/Pricing'))
const Reporting = lazy(() => import('Reporting/Reporting'))
const PeriodCards = lazy(() => import('Reporting/PeriodCards/components/PeriodCards'))
const MonthlyAllocation = lazy(() => import('Reporting/MonthlyAllocation/components/MonthlyAllocation'))
const Variance = lazy(() => import('Reporting/Variance/components/Variance'))
const BusinessIntelligence = lazy(() => import('Reporting/BusinessIntelligence/components/BusinessIntelligence'))

const CreditManagement = lazy(() => import('Admin/CreditManagement/components/CreditManagement'))
const Configuration = lazy(() => import('Admin/Configuration/components/Configuration'))
const MonthClose = lazy(() => import('Admin/MonthClose/components/MonthClose'))
const Users = lazy(() => import('Admin/Users/components/User'))
const WelcomePage = lazy(() => import('Welcome/WelcomePage'))
const Admin = lazy(() => import('Admin/Admin'))

const Budgets = lazy(() => import('ts/modules/budgets/Budgets'))
const AccountManagement = lazy(() => import('ts/modules/admin/sections/account-management/AccountManagement'))
const SavingsManagement = lazy(() => import('ts/modules/savings-management/SavingsManagement'))
const UserManagement = lazy(() => import('ts/modules/admin/sections/user-management/UserManagement'))
const PlatformManagement = lazy(() => import('ts/modules/admin/sections/platform-management/PlatformManagement'))
const AIModels = lazy(() => import('ts/modules/admin/sections/ai-models/AIModels'))
const CloudScal3Tools = lazy(() => import('ts/modules/admin/sections/cloudscal3-tools/CloudScal3Tools'))

const ICON_SIZE = 18

export const PUBLIC_ROUTES: RoutesMap = {
  LOGIN: {
    path: '/login',
    title: 'Login',
    component: Login
  },
  FORGOT_PASSWORD: {
    path: '/forgot-password',
    title: 'Forgot password',
    component: ForgotPassword
  },
  RESET_PASSWORD: {
    path: '/reset-password',
    title: 'Reset password',
    component: ResetPassword
  }
}

export const PRIVATE_ROUTES: RoutesMap = {
  WELCOME_PAGE: {
    path: '/welcome',
    title: 'Welcome',
    component: WelcomePage,
    permissions: [Roles.ADMINS]
  },
  DASHBOARD: {
    path: '/summary',
    title: 'Summary',
    component: Summary,
    permissions: [
      Roles.FINANCIAL_ADMINS,
      Roles.BU_MANAGERS,
      Roles.DEPARTMENT_MANAGERS,
      Roles.PORTFOLIO_MANAGERS,
      Roles.PRODUCT_MANAGERS,
      Roles.VENDOR_MANAGERS
    ],
    icon: <ClipboardList size={ICON_SIZE} />
  },

  SAVINGS_MANAGEMENT: {
    path: '/savings-management',
    title: 'Savings Management',
    component: SavingsManagement,
    permissions: [Roles.ADMINS, Roles.FINANCIAL_ADMINS, Roles.VENDOR_MANAGERS],
    icon: <PiggyBank size={ICON_SIZE} />
  },
  SPACES: {
    path: '/spaces',
    title: 'Spaces',
    component: Spaces,
    permissions: [],
    icon: <Wrench size={ICON_SIZE} />
  },
  BUDGETS: {
    path: '/budgets',
    title: 'Budgets',
    component: Budgets,
    permissions: [
      Roles.FINANCIAL_ADMINS,
      Roles.BU_MANAGERS,
      Roles.DEPARTMENT_MANAGERS,
      Roles.PORTFOLIO_MANAGERS,
      Roles.PRODUCT_MANAGERS,
      Roles.VENDOR_MANAGERS
    ],
    icon: <LayoutDashboard size={ICON_SIZE} />
  },
  REPORTING: {
    path: '/reporting',
    title: 'Reporting',
    component: Reporting,
    permissions: [
      Roles.ADMINS,
      Roles.FINANCIAL_ADMINS,
      Roles.BU_MANAGERS,
      Roles.DEPARTMENT_MANAGERS,
      Roles.PORTFOLIO_MANAGERS,
      Roles.PRODUCT_MANAGERS
    ],
    icon: <ChartPie size={ICON_SIZE} />,
    children: {
      PERIOD_CARDS: {
        path: 'period-cards',
        title: 'Period Cards',
        description: 'Business Week',
        component: PeriodCards,
        permissions: [
          Roles.FINANCIAL_ADMINS,
          Roles.BU_MANAGERS,
          Roles.DEPARTMENT_MANAGERS,
          Roles.PORTFOLIO_MANAGERS,
          Roles.PRODUCT_MANAGERS
        ],
        icon: <CreditCard size={ICON_SIZE} />
      },
      MONTHLY_ALLOCATION: {
        path: 'monthly-allocation',
        title: 'Monthly Allocation',
        description: 'Budget to Account',
        component: MonthlyAllocation,
        permissions: [
          Roles.FINANCIAL_ADMINS,
          Roles.BU_MANAGERS,
          Roles.DEPARTMENT_MANAGERS,
          Roles.PORTFOLIO_MANAGERS,
          Roles.PRODUCT_MANAGERS
        ],
        icon: <Calendar size={ICON_SIZE} />
      },
      VARIANCE: {
        path: 'variance',
        title: 'Variance',
        description: 'Budget to Spend',
        component: Variance,
        permissions: [
          Roles.FINANCIAL_ADMINS,
          Roles.BU_MANAGERS,
          Roles.DEPARTMENT_MANAGERS,
          Roles.PORTFOLIO_MANAGERS,
          Roles.PRODUCT_MANAGERS
        ],
        icon: <TrendingUp size={ICON_SIZE} />
      },
      BUSINESS_INTELLIGENCE: {
        path: 'business-intelligence',
        title: 'Business Intelligence',
        description: 'QuickSight',
        component: BusinessIntelligence,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.ADMINS, Roles.VENDOR_MANAGERS],
        icon: <Brain size={ICON_SIZE} />
      }
    }
  },
  VENDOR_MANAGEMENT: {
    path: '/vendor-management',
    title: 'Vendor Management',
    component: VendorManagement,
    permissions: [Roles.FINANCIAL_ADMINS, Roles.VENDOR_MANAGERS],
    icon: <UsersIcon size={ICON_SIZE} />,
    children: {
      BUDGET: {
        path: 'budget',
        title: 'Budget',
        description: 'Approved Annual',
        component: Budget,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.VENDOR_MANAGERS],
        icon: <Target size={ICON_SIZE} />
      },
      PRICING: {
        path: 'pricing',
        title: 'Pricing',
        description: 'Vendor Discounts',
        component: Pricing,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.VENDOR_MANAGERS],
        icon: <DollarSign size={ICON_SIZE} />
      }
    }
  },
  ADMIN: {
    path: '/admin',
    title: 'Admin',
    component: Admin,
    permissions: [Roles.ADMINS, Roles.FINANCIAL_ADMINS, Roles.BU_MANAGERS],
    icon: <Settings size={ICON_SIZE} />,
    children: {
      ACCOUNT_MANAGEMENT: {
        path: 'account-management',
        title: 'Account Management',
        description: 'Onboarding & Updates',
        component: AccountManagement,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.ADMINS],
        icon: <Briefcase size={ICON_SIZE} />
      },
      USER_MANAGEMENT: {
        path: 'user-management',
        title: 'User Management',
        description: 'Users to Roles',
        component: UserManagement,
        permissions: [Roles.FINANCIAL_ADMINS],
        icon: <UsersIcon size={ICON_SIZE} />
      },
      USERS: {
        path: 'users',
        title: 'Add Users',
        description: 'Onboarding',
        component: Users,
        permissions: [Roles.ADMINS],
        icon: <UsersIcon size={ICON_SIZE} />
      },
      CREDIT_MANAGEMENT: {
        path: 'credit-management',
        title: 'Credit Management',
        description: 'Credit to Budget',
        component: CreditManagement,
        permissions: [Roles.FINANCIAL_ADMINS],
        icon: <CreditCard size={ICON_SIZE} />
      },
      CONFIGURATION: {
        path: 'configuration',
        title: 'Configuration',
        description: 'AWS Services',
        component: Configuration,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.ADMINS],
        icon: <Settings size={ICON_SIZE} />
      },
      MONTH_CLOSE: {
        path: 'month-close',
        title: 'Month Close',
        description: 'AP & FP&A',
        component: MonthClose,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.BU_MANAGERS],
        icon: <Calendar size={ICON_SIZE} />
      },
      PLATFORM_MANAGEMENT: {
        path: 'platform-management/*',
        title: 'Platform Management',
        description: 'MAP Contracts & Tasks',
        component: PlatformManagement,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.ADMINS, Roles.BU_MANAGERS],
        icon: <CloudUpload size={ICON_SIZE} />
      },
      AI_MODELS: {
        path: 'ai-models',
        title: 'AI Models',
        description: 'Model Approvals',
        component: AIModels,
        permissions: [Roles.FINANCIAL_ADMINS],
        icon: <Brain size={ICON_SIZE} />
      },
      CLOUDSCAL3_TOOLS: {
        path: 'cloudscal3-tools',
        title: 'CloudScal3 Tools',
        description: 'Platform Utilities',
        component: CloudScal3Tools,
        permissions: [Roles.FINANCIAL_ADMINS, Roles.ADMINS, Roles.BU_MANAGERS],
        icon: <Wrench size={ICON_SIZE} />
      }
    }
  }
}
